'use client';

import CommonLoading from '@/components/layouts/CommonLoading/CommonLoading';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import type { UserDesignPrint } from '@/features/gfUserDesign/types';
import { sendClickShareDesignNavigationEvent } from '@/utils/ga';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { useState } from 'react';
import styles from './DesignPreviewImage.module.scss';

type DesignPreviewImageProps = {
  /** ユーザデザインのプリントエリア情報 */
  userPrints: UserDesignPrint[];
  workName: string;
  onImageClick: (imageUrl: string) => void;
};

/**
 * デザインのプレビュー画像
 * 親幅いっぱいに広がる（fill）。表示サイズは親コンテナで制御する。
 * 画像が複数ある場合はテキストボタンで画像を切り替え可能
 */
function DesignPreviewImage({ userPrints, workName, onImageClick }: DesignPreviewImageProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * プリントエリア切り替えボタンクリック時
   * 画像を前から順に切り替える。最後の画像の場合は最初に戻る（循環）
   */
  const handleSwitchPrintArea = () => {
    sendClickShareDesignNavigationEvent();
    setIsLoading(true);
    setCurrentIndex((prev) => {
      const nextIndex = prev + 1;
      return nextIndex >= userPrints.length ? 0 : nextIndex;
    });
  };

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  const showPrintAreaSwitchButton = userPrints.length > 1;
  const currentPrint = userPrints[currentIndex];
  const nextIndex = (currentIndex + 1) % userPrints.length;
  const switchButtonLabel = `${userPrints[nextIndex].name}を表示`;

  return (
    <div className={`${styles.previewImageContainer} ${styles.fillPreviewImageContainer}`}>
      <button
        type='button'
        className={styles.imageClickTarget}
        onClick={() => onImageClick(currentPrint.previewImageUrl)}
        aria-label='画像を拡大'
      >
        <NextImageView
          src={currentPrint.previewImageUrl}
          alt={workName}
          fill={true}
          objectFit='contain'
          sizes={getImageLayoutSizes({ sp: '300px', pc: '400px' })}
          onLoad={handleLoadingComplete}
        />
      </button>
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <CommonLoading />
        </div>
      )}
      {showPrintAreaSwitchButton && (
        <button
          type='button'
          className={styles.printAreaSwitchButton}
          onClick={handleSwitchPrintArea}
          aria-label={switchButtonLabel}
        >
          {switchButtonLabel}
        </button>
      )}
    </div>
  );
}

export default DesignPreviewImage;
